import React from 'react';
import { styled } from '@mui/material/styles';
import { Button as MuiButton } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';

// Version hash to ensure proper cache busting
const BUTTON_VERSION = 'v1.2.0';

// Helper function to determine if a color is light
const isLightColor = (hex) => {
  // Remove the hash if it exists
  hex = hex.replace('#', '');
  
  // Parse the RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  // Calculate relative luminance according to WCAG
  // https://www.w3.org/TR/WCAG20-TECHS/G17.html
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  
  // Return true if the color is light (luminance > 0.5)
  return luminance > 0.5;
};

// Styled button that matches the provided HTML/CSS template
const StyledButton = styled(MuiButton)(({ theme, customColor, secondaryColorProp, isSecondary, isDarkMode, isLightSecondary, isCompact }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: isCompact ? '4px 12px' : '8px 12px 8px 16px',
  gap: '8px',
  height: isCompact ? '32px' : '40px',
  minWidth: isCompact ? '100px' : '128px',
  border: 'none',
  background: isSecondary 
    ? `${secondaryColorProp}27` // Secondary color with 27 (15%) opacity
    : customColor 
      ? `${customColor}27` 
      : '#056bfa27', // Default blue with 27 (15%) opacity
  borderRadius: '20px',
  cursor: 'pointer',
  textTransform: 'none', // Prevent MUI uppercase transformation
  boxShadow: 'none',
  transition: 'background-color 0.3s ease',
  
  '& .label': {
    marginTop: '1px',
    fontSize: isCompact ? '14px' : '16px',
    lineHeight: isCompact ? '16px' : '20px',
    // Use secondary color for text by default unless specific conditions are met
    color: isSecondary 
      ? secondaryColorProp // If secondary variant button, use secondary color
      : customColor === '#13FFA3' 
        ? '#000000' // Special case for mint buttons
        : isLightSecondary
          ? '#333333' // If secondary color is very light, use dark text for better contrast
          : secondaryColorProp, // All other buttons use secondary color for text
    letterSpacing: isCompact ? '0.5px' : '1px',
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  
  '&:hover': {
    background: isSecondary 
      ? `${secondaryColorProp}49`
      : (customColor ? `${customColor}49` : '#056bfa49'), // 49 (29%) opacity on hover
    boxShadow: 'none',
  },
  
  '&.Mui-disabled': {
    background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
  },
  
  '&.MuiButton-outlined': {
    border: isSecondary 
      ? `1px solid ${secondaryColorProp}`
      : `1px solid ${customColor || '#056dfa'}`,
    background: 'transparent',
    '&:hover': {
      background: isSecondary 
        ? `${secondaryColorProp}10`
        : (customColor ? `${customColor}10` : '#056bfa10'),
    },
    // For outlined buttons, use the border color for text
    '& .label': {
      color: isSecondary 
        ? secondaryColorProp
        : (customColor || secondaryColorProp),
    }
  },

  // Special styling for mint green buttons
  ...(customColor === '#13FFA3' && {
    '& .label': {
      color: '#000000',
    }
  })
}));

/**
 * CustomButton - A reusable button component styled according to design specs
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Button text/content
 * @param {string} props.color - Custom color (hex code) to override default blue
 * @param {React.ReactNode} props.startIcon - Icon to display before text
 * @param {React.ReactNode} props.endIcon - Icon to display after text
 * @param {Function} props.onClick - Click handler function
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {Object} props.sx - Additional MUI sx styles to apply
 * @param {string} props.variant - Button variant ('contained', 'outlined', 'text', 'secondary')
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {string} props.type - Button type ('button', 'submit', 'reset')
 * @param {boolean} props.compact - Whether to use compact styling (smaller height and font)
 * @param {boolean} props.useSecondary - Whether to use secondary color instead of primary
 */
const CustomButton = React.forwardRef(({ 
  children, 
  color, 
  startIcon, 
  endIcon, 
  onClick, 
  disabled = false,
  sx = {},
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  type = 'button',
  compact = false,
  useSecondary = false,
  ...props 
}, ref) => {
  const { isDarkMode, primaryColor, secondaryColor } = useTheme();
  
  // Add effect to handle style refresh events
  React.useEffect(() => {
    const handleStyleRefresh = (event) => {
      // Force re-render when style refresh is requested
      const timestamp = event.detail?.timestamp;
      if (timestamp) {
        console.log('CustomButton style refresh requested:', timestamp);
        // This will trigger a re-render with fresh styles
      }
    };
    
    window.addEventListener('buttonStyleRefresh', handleStyleRefresh);
    
    return () => {
      window.removeEventListener('buttonStyleRefresh', handleStyleRefresh);
    };
  }, []);
  
  // Check if we should use secondary color (either by variant or useSecondary prop)
  const isSecondary = useSecondary || variant === 'secondary';
  
  // Use provided color, fall back to primary theme color, then default blue
  const buttonColor = color || primaryColor || '#056dfa';
  
  // Check if secondary color is too light for text
  const isLightSecondary = isLightColor(secondaryColor);
  
  // Special handling for mint green buttons - ensure black text
  const customSx = {
    ...sx,
    ...(buttonColor === '#13FFA3' && {
      '& .label': {
        color: '#000000',
      }
    }),
    // Apply text color overrides when explicitly set through style prop
    ...(props.style?.color && {
      '& .label': {
        color: props.style.color,
      }
    })
  };
  
  // Determine which MUI variant to use (since MUI doesn't have a "secondary" variant)
  const muiVariant = variant === 'secondary' ? 'contained' : variant;
  
  return (
    <StyledButton
      ref={ref}
      onClick={onClick}
      disabled={disabled}
      startIcon={startIcon}
      endIcon={endIcon}
      customColor={buttonColor}
      secondaryColorProp={secondaryColor}
      isSecondary={isSecondary}
      isDarkMode={isDarkMode}
      isLightSecondary={isLightSecondary}
      isCompact={compact || size === 'small'}
      variant={muiVariant}
      size={size}
      fullWidth={fullWidth}
      type={type}
      sx={customSx}
      data-button-version={BUTTON_VERSION}
      {...props}
    >
      <span className="label">{children}</span>
    </StyledButton>
  );
});

// Set display name for better debugging
CustomButton.displayName = 'CustomButton';

export default CustomButton; 